import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { parsers } from '../../helpers';
import { phonenumbers } from '../../utils';

import { mapVehicleData, track } from '../../actions/tracking';
import { LinkButton } from '../../components/button';
import { SpinnerInline } from '../../components/spinner';
import { getItem, getItemId } from '../../selectors/item';
import {
  getItemRetailerContactOptions,
  getItemRetailerPhoneParams,
} from '../../selectors/item-retailer';
import { isRetailerUsingFreespee } from '../../selectors/retailer';
import ClickToViewState from '../../shared/click-to-view-states';
import { conversionEvents, eventTypes } from '../../shared/tracking';

let stored: null | {
  state: {
    currentState: typeof ClickToViewState.Hidden;
    telephone: {
      national?: string;
      e164?: string;
    } | null;
  };
  id: string | null;
} = null;
const getIntialState = (id: string | null) => {
  if (!stored || id !== stored.id) {
    return { currentState: ClickToViewState.Hidden, telephone: null };
  }
  return stored.state;
};

interface IPhonenumberButton {
  isLight?: boolean;
  isMedium?: boolean;
  label: string;
  trackingContext?: string;
}

const PhonenumberButton = ({ label, isMedium, isLight, trackingContext }: IPhonenumberButton) => {
  const { t } = useTranslation(['item']);
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const id = useSelector(getItemId);
  const contactOptions = useSelector(getItemRetailerContactOptions);
  const getParams = useSelector(getItemRetailerPhoneParams);
  const isGenerationActive = useSelector(isRetailerUsingFreespee);
  const actions = bindActionCreators(
    {
      track,
    },
    dispatch,
  );

  const [state, setState] = useState(getIntialState(id));
  if (!contactOptions) return null;

  useEffect(() => {
    if (state.telephone) {
      stored = { state: { ...state }, id };
    }
  }, [state.telephone]);

  const trackVisible = () =>
    actions.track(eventTypes.CONVERSION, {
      item: mapVehicleData(item, true),
      conversionType: conversionEvents.CONVERSION_PHONENUMBER_VISIBLE,
      information: { context: trackingContext },
    });

  const onClick = (e: React.MouseEvent) => {
    if (state.currentState === ClickToViewState.Processing) {
      return false;
    }

    if (contactOptions?.telephone?.e164) {
      if (state.currentState === ClickToViewState.Hidden && isGenerationActive) {
        e.stopPropagation();
        e.preventDefault();

        trackVisible();

        phonenumbers.convert(
          contactOptions.telephone.international || '',
          getParams,
          (generated) => {
            const phonenumber = parsers.parsePhoneNumber(generated);
            setState({ currentState: ClickToViewState.Visible, telephone: phonenumber });
          },
        );
      } else if (state.currentState === ClickToViewState.Hidden) {
        e.stopPropagation();
        e.preventDefault();

        trackVisible();

        setState({ currentState: ClickToViewState.Visible, telephone: contactOptions.telephone });
      }
    }

    if (state.currentState === ClickToViewState.Visible) {
      actions.track(eventTypes.OTHER, {
        item: mapVehicleData(item, true),
        conversionType: conversionEvents.CONVERSION_PHONENUMBER_CALL,
      });
    }
  };

  if (!contactOptions?.telephone?.e164) return null;

  if (state.currentState === ClickToViewState.Visible && state.telephone) {
    const { e164, national } = state.telephone;
    return (
      <LinkButton
        href={e164}
        title={t('callPhoneNumber', { ns: 'item', phoneNumber: national })}
        modifiers={`${isLight ? 'action-light' : 'action'} full-width ${isMedium ? 'medium' : ''}`}
        onClick={onClick}
      >
        <i className="icon-phone m-r-half" />
        {national}
      </LinkButton>
    );
  }

  return (
    <button
      type="button"
      data-wx-button={`${isLight ? 'action-light' : 'action'} full-width ${isMedium ? 'medium' : ''}`}
      title={label}
      onClick={onClick}
    >
      <i className="icon-phone m-r-half" />
      {state.currentState === ClickToViewState.Processing ? (
        <SpinnerInline modifiers="inverted" />
      ) : (
        label
      )}
    </button>
  );
};

export default PhonenumberButton;
